// ViewModalSortsSection.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Icons
import Icon from 'svg/Icon';

// Components
import { buildDefaultSort } from '../../viewModalFilters';
import Button from 'components/button/Button';
import ModalField from 'components/modal/field/ModalField';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import SelectInput from 'components/selectinput/SelectInput';

/**
 * ViewModalSortsSection
 *
 * Renders the Sorts block inside ViewModal, including the header,
 * empty state messaging, and a list of sort rows.
 *
 * @returns {ReactNode} The rendered component.
 */
const ViewModalSortsSection = ({
    sorts,
    setSorts,
    filterableFields,
    fieldOptions,
    label = 'Sorts',
    hint = 'Sorts allow for ordering the data according to specific criteria.',
    emptyHint = 'No sorts added yet.',
    addButtonLabel = '+ Add Sort',
}) => {

    // ----- CONTEXT ----- //

    // Theme Context

    const { theme } = useTheme();

    // ----- RENDER ----- //

    return (

        <ModalField>

            <ModalLabel label={label} />
            <ModalHint hint={hint} />

            {filterableFields.length === 0 && (
                <ModalHint hint="No sortable fields available." />
            )}

            {filterableFields.length > 0 && sorts.length === 0 && (
                <ModalHint hint={emptyHint} />
            )}

            {sorts.length > 0 && (
                <div className="view-modal-rows">
                    {sorts.map((sort, index) => (
                        <div className="view-modal-row" key={`sort_${index}`}>
                            <SelectInput
                              style={{
                                    fontSize: '12px',
                                }}
                              value={sort.field || ''}
                              options={fieldOptions}
                              onChange={(e) => {
                                    const nextFieldKey = e.target.value;
                                    setSorts((prev) => prev.map((item, idx) => (
                                        idx === index ? { ...item, field: nextFieldKey } : item
                                    )));
                                }}
                            />

                            <SelectInput
                              style={{
                                    fontSize: '12px',
                                }}
                              value={sort.direction || 'asc'}
                              options={[
                                    { value: 'asc', label: 'Ascending' },
                                    { value: 'desc', label: 'Descending' },
                                ]}
                              onChange={(e) => {
                                    const nextDirection = e.target.value;
                                    setSorts((prev) => prev.map((item, idx) => (
                                        idx === index ? { ...item, direction: nextDirection } : item
                                    )));
                                }}
                            />

                            <div className="view-modal-row-spacer" />
                            <div className="view-modal-row-spacer" />

                            <div
                              className="view-modal-remove-button"
                              style={{
                                color: theme.foregroundMuted,
                              }}
                              onClick={() => setSorts((prev) => prev.filter((_, idx) => idx !== index))}
                            >
                                <Icon
                                  iconKey="do_not_disturb_on"
                                  width={16}
                                  height={16}
                                  color={theme.foregroundMuted}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <div className="view-modal-add-button">
                <Button
                  label={addButtonLabel}
                  fontSize="12px"
                  fontWeight="400"
                  foregroundColor={theme.foregroundMuted}
                  backgroundColor="transparent"
                  height="30px"
                  padding="0 10px"
                  disabled={filterableFields.length === 0}
                  onClick={() => setSorts((prev) => [...prev, buildDefaultSort(filterableFields)])}
                />
            </div>

        </ModalField>
    );
};

// Dev Tools
ViewModalSortsSection.displayName = 'ViewModalSortsSection';

export default ViewModalSortsSection;
